import {
  cn,
  FormSection,
  RadioCardList,
  Skeleton,
  type RadioCardListOption,
} from '@chekinapp/ui';
import {useEffect, useId, type ReactNode} from 'react';
import {
  Controller,
  type Control,
  type UseFormSetValue,
  type UseFormWatch,
} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import type {TFunction} from 'i18next';
import {Contact, Globe, IdCard, Zap} from 'lucide-react';
import {useGuestSchema} from '../GuestSchemaContext';
import {CountriesSelect} from '../../../components/CountriesSelect';
import {
  isPassportDocType,
  getDocumentsListAsOptions,
  GUEST_FORM_NAMES,
  DocumentChoice,
} from '@chekin/core';
import type {RegisteredFieldHandlers} from '../virtualKeyboard/VirtualKeyboardContext';
import {DocumentSelectionFormTypes} from './types';
import {useInitDocumentType, useInitNationality} from './init';
import {DocumentTypeTiles} from './DocumentTypeTiles';
import {getDocumentTypeIcon} from './getDocumentTypeIcon';

const getDocumentCardOptions = (
  documentOptions: ReturnType<typeof getDocumentsListAsOptions>,
  t: TFunction,
  {withHints, withSelfie}: {withHints?: boolean; withSelfie?: boolean},
): RadioCardListOption[] =>
  documentOptions.map(option => ({
    value: option.value,
    label: option.label,
    description: withHints ? (
      <>
        <span className="block">
          {[
            t(
              option.data?.two_sides
                ? 'document_hint.two_sides'
                : 'document_hint.one_side',
            ),
            withSelfie ? t('document_hint.and_selfie') : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </span>
        {isPassportDocType(option.value) && (
          <span className="mt-[3px] inline-flex items-center gap-1 text-[10.5px] font-bold text-[var(--chekin-color-brand-blue)]">
            <Zap className="size-[11px]" />
            {t('recommended')}
          </span>
        )}
      </>
    ) : undefined,
    icon: getDocumentTypeIcon(option.value),
  }));

const THREE_COLUMN_TILE_COUNT = 3;
const THREE_COLUMN_TILE_CLASS =
  'lg:min-h-[64px] lg:pl-3.5 lg:pr-10 lg:[&_[role=radio]]:right-3.5';

const GRID_LABEL_CLASS =
  'm-0 mb-2 text-[12px] font-semibold uppercase tracking-[0.04em] text-[var(--chekin-text-secondary)]';

function DocumentTileSkeleton() {
  return (
    <div className="grid grid-cols-2 gap-3">
      <Skeleton className="h-[60px] rounded-[14px]" />
      <Skeleton className="h-[60px] rounded-[14px]" />
    </div>
  );
}

function DocumentTypeSkeleton() {
  return (
    <div className="flex flex-col">
      {[0, 1].map(row => (
        <div
          key={row}
          className="flex min-h-[66px] items-center gap-3.5 border-t border-[var(--radio-card-border)] px-[18px] py-3.5 first:border-t-0"
        >
          <Skeleton className="h-10 w-10 flex-none rounded-[10px]" />
          <Skeleton className="h-4 w-32 flex-1" />
          <Skeleton className="h-5 w-5 flex-none rounded-full" />
        </div>
      ))}
    </div>
  );
}

export type DocumentSelectionVirtualKeyboard = {
  register: (fieldId: string) => RegisteredFieldHandlers;
  handleFocus: (fieldId: string) => void;
  handleBlur: (fieldId: string) => void;
  node?: ReactNode;
};

const NOOP_KEYBOARD: DocumentSelectionVirtualKeyboard = {
  register: () => ({
    onChange:
      <T,>(userOnChange?: (option: T | null) => void) =>
      (option: T | null) => {
        userOnChange?.(option);
      },
  }),
  handleFocus: () => {},
  handleBlur: () => {},
};

type Props = {
  control: Control<DocumentSelectionFormTypes>;
  watch: UseFormWatch<DocumentSelectionFormTypes>;
  setValue: UseFormSetValue<DocumentSelectionFormTypes>;
  countryMenuClassName?: string;
  countryMenuPortal?: boolean;
  documents?: DocumentChoice[];
  isDocumentsLoading?: boolean;
  withHints?: boolean;
  withSelfie?: boolean;
  layout?: 'list' | 'tiles' | 'grid';
  virtualKeyboard?: DocumentSelectionVirtualKeyboard;
};

function DocumentSelectionFields({
  control,
  watch,
  setValue,
  countryMenuClassName,
  countryMenuPortal,
  documents,
  isDocumentsLoading,
  withHints,
  withSelfie,
  layout = 'list',
  virtualKeyboard = NOOP_KEYBOARD,
}: Props) {
  const {t} = useTranslation();
  const documentTypeLabelId = useId();
  const nationalityLabelId = useId();

  const {register, handleFocus, handleBlur, node: keyboard} = virtualKeyboard;
  const nationalityVirtualKeyboardField = register('nationality');

  const {
    reservationDocumentsList,
    setNationality: setGuestNationality,
    isGuestSchemaFetching,
  } = useGuestSchema();

  const isExternalDocuments = documents !== undefined;
  const resolvedDocuments = documents ?? reservationDocumentsList;
  const isDocumentsFetching = isDocumentsLoading ?? isGuestSchemaFetching;

  const nationality = watch(GUEST_FORM_NAMES.nationality);
  const documentType = watch(GUEST_FORM_NAMES.document_type);
  const documentOptions = getDocumentsListAsOptions(resolvedDocuments);

  useInitNationality({setValue});
  useInitDocumentType({
    setValue,
    options: documentOptions,
    nationality,
    value: documentType,
  });

  useEffect(() => {
    if (isExternalDocuments) return;
    setGuestNationality?.(nationality?.value);
  }, [isExternalDocuments, nationality, setGuestNationality]);

  const nationalityField = (
    <Controller
      name={GUEST_FORM_NAMES.nationality}
      rules={{required: t('required')}}
      control={control}
      render={({
        field: {onChange, onBlur: controllerOnBlur, ...otherFields},
        fieldState: {error},
      }) => {
        return (
          <CountriesSelect
            label={t('country_of_nationality')}
            placeholder={t('country_of_nationality')}
            menuClassName={countryMenuClassName}
            menuPortal={countryMenuPortal}
            error={error?.message}
            inputValue={nationalityVirtualKeyboardField.value || undefined}
            onInputChange={nationalityVirtualKeyboardField.onInputChange}
            onFocus={() => handleFocus('nationality')}
            onBlur={() => {
              controllerOnBlur();
              handleBlur('nationality');
            }}
            onChange={value => {
              nationalityVirtualKeyboardField.onChange?.(() => {
                onChange(value);
                setValue(GUEST_FORM_NAMES.document_type, null);
              })(value);
            }}
            {...otherFields}
          />
        );
      }}
    />
  );

  const handleDocumentChange =
    (onChange: (value: (typeof documentOptions)[number] | null) => void) =>
    (value: string) =>
      onChange(documentOptions.find(option => option.value === value) ?? null);

  if (layout === 'grid') {
    return (
      <>
        <div className="flex flex-col">
          <p id={nationalityLabelId} className={GRID_LABEL_CLASS}>
            {t('nationality')}
          </p>
          <div
            role="group"
            aria-labelledby={nationalityLabelId}
            className="iv-document-setup-view__nationality [--field-max-width:100%]"
          >
            {nationalityField}
          </div>

          {!nationality && (
            <p className="m-0 mt-4 flex items-center gap-3 rounded-[12px] bg-[var(--chekin-neutral-100)] px-4 py-3.5 text-[14px] leading-5 text-[var(--chekin-text-secondary)]">
              <IdCard
                aria-hidden
                className="size-5 flex-none text-[var(--chekin-color-brand-blue)]"
              />
              {t('identity-verification:choose_nationality_first_hint')}
            </p>
          )}

          {nationality && (
            <div className="iv-document-setup-view__document">
              <p id={documentTypeLabelId} className={cn(GRID_LABEL_CLASS, 'mt-4')}>
                {t('document_type')}
              </p>
              {isDocumentsFetching ? (
                <DocumentTileSkeleton />
              ) : (
                <Controller
                  name={GUEST_FORM_NAMES.document_type}
                  rules={{required: t('required')}}
                  control={control}
                  render={({field, fieldState: {error}}) => (
                    <div role="group" aria-labelledby={documentTypeLabelId}>
                      <RadioCardList
                        layout="grid"
                        name={GUEST_FORM_NAMES.document_type}
                        optionClassName={
                          documentOptions.length === THREE_COLUMN_TILE_COUNT
                            ? THREE_COLUMN_TILE_CLASS
                            : undefined
                        }
                        options={documentOptions.map(option => ({
                          value: option.value,
                          label: option.label,
                          icon: getDocumentTypeIcon(option.value),
                        }))}
                        value={field.value?.value ?? null}
                        onChange={handleDocumentChange(field.onChange)}
                        error={error?.message}
                      />
                    </div>
                  )}
                />
              )}
            </div>
          )}
        </div>
        {keyboard}
      </>
    );
  }

  if (layout === 'tiles') {
    return (
      <>
        <div className="flex flex-col gap-4">
          <div className="iv-document-setup-view__nationality [--field-max-width:100%]">
            {nationalityField}
          </div>

          {nationality && (
            <div className="iv-document-setup-view__document flex flex-col gap-2">
              <p
                id={documentTypeLabelId}
                className="m-0 text-[14px] font-semibold leading-5 text-[var(--chekin-text-secondary)]"
              >
                {t('document_type')}
              </p>
              {isDocumentsFetching ? (
                <DocumentTileSkeleton />
              ) : (
                <Controller
                  name={GUEST_FORM_NAMES.document_type}
                  rules={{required: t('required')}}
                  control={control}
                  render={({field, fieldState: {error}}) => (
                    <DocumentTypeTiles
                      name={GUEST_FORM_NAMES.document_type}
                      labelledBy={documentTypeLabelId}
                      options={documentOptions}
                      value={field.value?.value ?? null}
                      onChange={handleDocumentChange(field.onChange)}
                      error={error?.message}
                    />
                  )}
                />
              )}
            </div>
          )}
        </div>
        {keyboard}
      </>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-[18px] sm:gap-6">
        <FormSection className="iv-document-setup-view__nationality">
          <FormSection.Header icon={<Globe />}>{t('nationality')}</FormSection.Header>
          <FormSection.Body>{nationalityField}</FormSection.Body>
        </FormSection>

        {nationality && (
          <FormSection className="iv-document-setup-view__document !rounded-b-none">
            <FormSection.Header icon={<Contact />}>
              {t('document_type')}
            </FormSection.Header>
            <FormSection.Body className="p-0">
              {isDocumentsFetching ? (
                <DocumentTypeSkeleton />
              ) : (
                <Controller
                  name={GUEST_FORM_NAMES.document_type}
                  rules={{required: t('required')}}
                  control={control}
                  render={({field, fieldState: {error}}) => (
                    <RadioCardList
                      name={GUEST_FORM_NAMES.document_type}
                      options={getDocumentCardOptions(documentOptions, t, {
                        withHints,
                        withSelfie,
                      })}
                      value={field.value?.value ?? null}
                      onChange={handleDocumentChange(field.onChange)}
                      error={error?.message}
                      className="rounded-none border-0 [box-shadow:none]"
                    />
                  )}
                />
              )}
            </FormSection.Body>
          </FormSection>
        )}
      </div>
      {keyboard}
    </>
  );
}

export {DocumentSelectionFields};
